import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ResponsiveHeatMap } from '@nivo/heatmap'
import { BasicTooltip } from '@nivo/tooltip'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { useAnalytics } from 'modules/reviews/pages/performance/Show/Analytics/analyticsContext'
import type AnalyticResult from 'modules/reviews/store/models/AnalyticResult'

import type ScoreForm from '../ScoreForm'
import { useData, useKeys } from '../hooks'
import LeftTick from './LeftTick'
import TopTick from './TopTick'

interface Props {
  numericResult: AnalyticResult
  scoreForm: ScoreForm
  title: string
}

const MIN_HEIGHT = 400
const ROW_HEIGHT = 50
const MARGIN_VERTICAL = 100
const MARGIN_HORIZONTAL = 150

const Heatmap = ({ numericResult, scoreForm, title }: Props) => {
  const { cycle } = useAnalytics()
  const questionGroups = numericResult.questionGroupsInCycle(cycle)
  const heatmapData = useData(numericResult, scoreForm, questionGroups)
  const keysData = useKeys(numericResult, scoreForm)

  const borderStyle = {
    color: 'grey400',
    width: 's1',
    style: 'solid',
  } as const

  if (!questionGroups) return null

  const { data, maxValue, minValue } = heatmapData.get()
  const keys = keysData.get()
  const keysWithPosition: string[] = []

  // @nivo/heatmap does not allow us to render questions/sections with the
  // same label. We are giving each question/section a position in order
  // to differentiate them.
  keys.forEach((key, index) => {
    const position = index + 1
    keysWithPosition.push(position.toString() + '. ' + key)
  })

  const NOT_ANSWERED = '-'

  const BarTooltip = (props: { yKey: string; value: string; id: string }) => {
    const index = Number(props.id.split('.')[0])
    const keys = props.yKey + ' - ' + keysWithPosition[index - 1]
    const value = '\n' + i18n.t('surveys.review.analytics.value') + ' ' + props.value
    return <BasicTooltip id={keys} value={value} />
  }

  return (
    <Box>
      <Box
        border={{
          top: borderStyle,
          left: borderStyle,
          right: borderStyle,
        }}
        borderRadius={{
          topRight: 'abs008',
          topLeft: 'abs008',
        }}
        paddingY="s16"
        paddingX="s24"
        background="grey200"
      >
        <Text.Normal weight="medium">{title}</Text.Normal>
      </Box>
      <Box
        border={{
          all: borderStyle,
        }}
        style={{
          height: `${Math.max(MIN_HEIGHT, data.length * ROW_HEIGHT) + MARGIN_VERTICAL * 2}px`,
        }}
      >
        <ResponsiveHeatMap
          data={data}
          keys={keysWithPosition}
          minValue={minValue}
          maxValue={maxValue}
          indexBy="targetName"
          margin={{
            top: MARGIN_VERTICAL,
            right: MARGIN_HORIZONTAL,
            bottom: MARGIN_VERTICAL,
            left: MARGIN_HORIZONTAL,
          }}
          forceSquare
          axisTop={{
            tickSize: 0,
            renderTick: TopTick,
          }}
          axisLeft={{
            tickSize: 0,
            renderTick: LeftTick,
          }}
          cellOpacity={1}
          cellBorderColor={{
            from: 'color',
            modifiers: [['darker', 0.4]],
          }}
          labelTextColor={{
            from: 'color',
            modifiers: [['darker', 1.8]],
          }}
          animate
          motionStiffness={80}
          motionDamping={9}
          hoverTarget="cell"
          cellHoverOthersOpacity={0.25}
          colors="GnBu"
          // TODO: This prop doesn't exist on nivo, we may add it to their types
          // DO NOT REMOVE IT! It will break the chart for non-answered questions
          tooltip={BarTooltip}
          label={(datum: any, key: any) => (isNaN(datum[key]) ? '' : datum[key])}
          tooltipFormat={(value: number) => (isNaN(value) ? NOT_ANSWERED : value)}
        />
      </Box>
    </Box>
  )
}

export default observer(Heatmap)
